import axios from 'axios';
import { getTenantId } from '../tenant/tenantService.js';
import { secureLocalStorage } from '@/utils/crypto';

const BENEFITS_URL = process.env.REACT_APP_ENGAGEMENT_URL;
const REDEMPTION_CACHE_KEY = 'stamp_redemption_cache';
const REDEMPTION_CACHE_TIMESTAMP_KEY = 'stamp_redemption_cache_timestamp';
const REDEMPTION_PROGRESS_KEY = 'stamp_redemption_progress';
const REDEMPTION_COMPLETED_KEY = 'stamp_redemption_completed';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos

// Funções auxiliares para cache
const getRedemptionCache = () => {
  try {
    const cache = sessionStorage.getItem(REDEMPTION_CACHE_KEY);
    const timestamp = sessionStorage.getItem(REDEMPTION_CACHE_TIMESTAMP_KEY);
    
    if (!cache || !timestamp) return null;
    
    const now = Date.now();
    const cacheTime = parseInt(timestamp);
    
    // Verificar se o cache ainda é válido
    if (now - cacheTime > CACHE_DURATION) {
      clearRedemptionCache();
      return null;
    }
    
    return JSON.parse(cache);
  } catch (error) {
    console.error('Error reading redemption cache:', error);
    return null;
  }
};

const setRedemptionCache = (benefitId, data) => {
  try {
    const cache = getRedemptionCache() || {};
    cache[benefitId] = {
      data,
      timestamp: Date.now()
    };
    
    sessionStorage.setItem(REDEMPTION_CACHE_KEY, JSON.stringify(cache));
    sessionStorage.setItem(REDEMPTION_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error setting redemption cache:', error);
  }
};

const clearRedemptionCache = () => {
  try {
    sessionStorage.removeItem(REDEMPTION_CACHE_KEY);
    sessionStorage.removeItem(REDEMPTION_CACHE_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Error clearing redemption cache:', error);
  }
};

// Funções para gerenciar estado de resgate em andamento
const getRedemptionProgress = () => {
  try {
    const progress = sessionStorage.getItem(REDEMPTION_PROGRESS_KEY);
    return progress ? JSON.parse(progress) : {};
  } catch (error) {
    console.error('Error reading redemption progress:', error);
    return {};
  }
};

const setRedemptionProgress = (benefitId, inProgress) => {
  try {
    const progress = getRedemptionProgress();
    if (inProgress) {
      progress[benefitId] = Date.now();
    } else {
      delete progress[benefitId];
    }
    sessionStorage.setItem(REDEMPTION_PROGRESS_KEY, JSON.stringify(progress));
  } catch (error) {
    console.error('Error setting redemption progress:', error);
  }
};

// Funções para gerenciar estado de resgate concluído
const getCompletedRedemptions = () => {
  try {
    const completed = sessionStorage.getItem(REDEMPTION_COMPLETED_KEY);
    return completed ? JSON.parse(completed) : {};
  } catch (error) {
    console.error('Error reading completed redemptions:', error);
    return {};
  }
};

const setCompletedRedemption = (benefitId, completed) => {
  try {
    const completedRedemptions = getCompletedRedemptions();
    if (completed) {
      completedRedemptions[benefitId] = Date.now();
    } else {
      delete completedRedemptions[benefitId];
    }
    sessionStorage.setItem(REDEMPTION_COMPLETED_KEY, JSON.stringify(completedRedemptions));
  } catch (error) {
    console.error('Error setting completed redemption:', error);
  }
};

export const benefitsService = {
  async listBenefits(campaignId, partnerId = null, pageSize = 15, pageNumber = 1) {
    try {
      const response = await axios.post(
        `${BENEFITS_URL}/api/Benefit/list?pageSize=${pageSize}&pageNumber=${pageNumber}`,
        {
          campaignId,
          ...(partnerId && { partnerId })
        },
        {
          headers: {
            'x-tenant-id': getTenantId(),
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${secureLocalStorage.getItem('idToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching benefits list:', error);
      
      // Tratar especificamente erro 404 (endpoint não encontrado)
      if (error.response?.status === 404) {
        const customError = new Error('O serviço de benefícios não está disponível no momento.');
        customError.isNotFound = true;
        customError.status = 404;
        throw customError;
      }
      
      throw error;
    }
  },

  async getBenefitDetails(benefitId) {
    try {
      const response = await axios.post(
        `${BENEFITS_URL}/api/Benefit/details`,
        { benefitId },
        {
          headers: {
            'x-tenant-id': getTenantId(),
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${secureLocalStorage.getItem('idToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching benefit details:', error);
      throw error;
    }
  },

  async redeemBenefit(benefitId) {
    try {
      // Verificar cache primeiro
      const cachedData = getRedemptionCache();
      if (cachedData && cachedData[benefitId] && cachedData[benefitId].data) {
        console.log('Using cached redemption data for benefit:', benefitId);
        return cachedData[benefitId].data;
      }

      const response = await axios.post(
        `${BENEFITS_URL}/api/Benefit/redemption`,
        { benefitId },
        {
          headers: {
            'x-tenant-id': getTenantId(),
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${secureLocalStorage.getItem('idToken')}`
          }
        }
      );
      
      // Armazenar no cache apenas se a resposta for bem-sucedida
      if (response.data) {
        setRedemptionCache(benefitId, response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error redeeming benefit:', error);
      throw error;
    }
  },

  async getRedemptionInfo(transactionId) {
    try {
      const response = await axios.get(
        `${BENEFITS_URL}/api/Benefit/redemption?pageSize=50&pageNumber=1`,
        {
          headers: {
            'x-tenant-id': getTenantId(),
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${secureLocalStorage.getItem('idToken')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching redemption info:', error);
      throw error;
    }
  },

  // Funções para gerenciar cache
  getCachedRedemption(benefitId) {
    const cache = getRedemptionCache();
    return cache && cache[benefitId] && cache[benefitId].data ? cache[benefitId].data : null;
  },

  clearCache() {
    clearRedemptionCache();
  },

  clearBenefitCache(benefitId) {
    try {
      const cache = getRedemptionCache();
      if (cache && cache[benefitId]) {
        delete cache[benefitId];
        sessionStorage.setItem(REDEMPTION_CACHE_KEY, JSON.stringify(cache));
        console.log('Cache limpo para benefício específico:', benefitId);
      }
    } catch (error) {
      console.error('Error clearing specific benefit cache:', error);
    }
  },

  isRedemptionCached(benefitId) {
    const cache = getRedemptionCache();
    return cache && cache[benefitId] && cache[benefitId].data ? true : false;
  },

  // Funções para gerenciar estado de resgate em andamento
  isRedemptionInProgress(benefitId) {
    const progress = getRedemptionProgress();
    return progress && progress[benefitId] ? true : false;
  },

  setRedemptionInProgress(benefitId, inProgress) {
    setRedemptionProgress(benefitId, inProgress);
  },

  // Funções para gerenciar estado de resgate concluído
  hasCompletedRedemption(benefitId) {
    const completed = getCompletedRedemptions();
    return completed && completed[benefitId] ? true : false;
  },

  setRedemptionCompleted(benefitId, completed) {
    setCompletedRedemption(benefitId, completed);
  },

  // Limpar todos os estados de resgate
  clearAllRedemptionStates() {
    try {
      sessionStorage.removeItem(REDEMPTION_PROGRESS_KEY);
      sessionStorage.removeItem(REDEMPTION_COMPLETED_KEY);
    } catch (error) {
      console.error('Error clearing redemption states:', error);
    }
  }
}; 
